import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Mail, ArrowLeft, Sparkles, Handshake, Copy, Check } from 'lucide-react';

// Round 47 — Partner landing page (/partner).
// Public, unauthenticated, intentionally minimal: a "Coming Soon"
// message plus a single call-to-action — drop a "Hi" at
// admin@astroconsult.app to start the conversation.
//
// Visual language matches the marketing site (obsidian bg, marigold
// accents, Cormorant serif headings) so the page feels native rather
// than a forgotten dead-end.
const PARTNER_EMAIL = 'admin@astroconsult.app';

export default function Partner() {
    const [copied, setCopied] = useState(false);

    // Keep the document title in sync — helps when this URL is shared
    // on WhatsApp / LinkedIn / Twitter.
    useEffect(() => {
        const prev = document.title;
        document.title = 'Partner with AstroConsult — Coming Soon';
        return () => { document.title = prev; };
    }, []);

    const copyEmail = async () => {
        try {
            await navigator.clipboard.writeText(PARTNER_EMAIL);
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        } catch {
            // Clipboard unavailable — fall through silently. The mailto
            // CTA still works.
        }
    };

    const subject = encodeURIComponent('Hi — interested in partnering with AstroConsult');
    const body = encodeURIComponent(
        "Hi AstroConsult team,\n\nI'd like to explore partnering with you. " +
        "Here's a quick intro about me / my organization:\n\n• \n\nLooking forward to hearing from you."
    );
    const mailto = `mailto:${PARTNER_EMAIL}?subject=${subject}&body=${body}`;

    return (
        <div
            className="min-h-screen bg-obsidian text-parchment relative overflow-hidden"
            data-testid="partner-page"
        >
            {/* Decorative starfield (no images — pure CSS, keeps the page
                fast and offline-friendly). */}
            <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 opacity-[0.35]"
                style={{
                    backgroundImage:
                        'radial-gradient(circle at 20% 20%, rgba(245,158,11,0.15), transparent 40%),' +
                        'radial-gradient(circle at 80% 60%, rgba(245,158,11,0.10), transparent 45%),' +
                        'radial-gradient(circle at 60% 90%, rgba(245,158,11,0.08), transparent 50%)',
                }}
            />

            {/* Top bar — back-to-home only; keep the page distraction-free. */}
            <header className="relative z-10 max-w-6xl mx-auto px-6 py-6 flex items-center justify-between">
                <Link
                    to="/"
                    className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-marigold transition-colors"
                    data-testid="partner-back-home"
                >
                    <ArrowLeft size={16} /> Back to home
                </Link>
                <Link
                    to="/"
                    className="font-serif text-marigold text-xl tracking-wide"
                    data-testid="partner-logo"
                >
                    AstroConsult
                </Link>
            </header>

            <main
                className="relative z-10 max-w-3xl mx-auto px-6 pt-12 pb-24 md:pt-20 md:pb-32 text-center"
            >
                {/* Overline */}
                <div
                    className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-marigold/30 bg-marigold/5 text-marigold text-xs tracking-widest uppercase mb-8"
                    data-testid="partner-overline"
                >
                    <Sparkles size={12} /> Partnership Program
                </div>

                {/* Hero copy */}
                <h1
                    className="font-serif text-4xl sm:text-5xl lg:text-6xl text-parchment leading-tight"
                    data-testid="partner-heading"
                >
                    Something special is <span className="text-marigold">brewing</span>.
                </h1>
                <p
                    className="mt-6 text-base sm:text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed"
                    data-testid="partner-tagline"
                >
                    We&apos;re building a partnership program for astrologers, healers,
                    creators, and platforms who care about doing this work seriously.
                    Details are coming soon.
                </p>

                {/* Coming soon badge */}
                <div className="mt-10">
                    <div
                        className="inline-flex items-center gap-3 px-6 py-3 rounded-sm border border-marigold/40 bg-elevated/40"
                        data-testid="partner-coming-soon"
                    >
                        <Handshake className="text-marigold" size={20} />
                        <span className="font-serif text-xl text-parchment">Coming soon</span>
                    </div>
                </div>

                {/* Email CTA card */}
                <Card
                    className="mt-14 bg-surface border-marigold/15 rounded-sm p-7 md:p-9 text-left max-w-xl mx-auto"
                    data-testid="partner-cta-card"
                >
                    <div className="flex items-center gap-2 text-marigold mb-3">
                        <Mail size={18} />
                        <div className="overline text-marigold/90">In the meantime</div>
                    </div>

                    <p className="font-serif text-2xl md:text-3xl text-parchment leading-snug">
                        Interested in partnering with us? <br />
                        Drop a <span className="text-marigold italic">&ldquo;Hi&rdquo;</span>.
                    </p>

                    <p className="text-sm text-slate-400 mt-4">
                        Tell us a little about you and how you&apos;d like to collaborate.
                        We read every email personally — usually within 2–3 working days.
                    </p>

                    <div className="mt-6 flex flex-col sm:flex-row gap-3">
                        <a
                            href={mailto}
                            className="flex-1"
                            data-testid="partner-mailto-link"
                        >
                            <Button
                                className="w-full bg-marigold hover:bg-marigold-hover text-obsidian rounded-sm h-12 text-base"
                                data-testid="partner-mailto-btn"
                            >
                                <Mail size={16} className="mr-2" /> Email us
                            </Button>
                        </a>
                        <Button
                            onClick={copyEmail}
                            className="flex-1 bg-marigold/10 hover:bg-marigold/20 text-marigold border border-marigold/30 rounded-sm h-12 text-base"
                            data-testid="partner-copy-email-btn"
                        >
                            {copied
                                ? <><Check size={16} className="mr-2" /> Copied</>
                                : <><Copy size={16} className="mr-2" /> Copy email</>}
                        </Button>
                    </div>

                    <div
                        className="mt-5 px-4 py-3 rounded-sm bg-obsidian/60 border border-marigold/10 text-sm text-slate-300 font-mono break-all text-center"
                        data-testid="partner-email-display"
                    >
                        {PARTNER_EMAIL}
                    </div>
                </Card>

                {/* Tiny footer line */}
                <p className="text-xs text-slate-500 mt-10" data-testid="partner-footer">
                    AstroConsult · Vedic & KP astrology, made earnest.
                </p>
            </main>
        </div>
    );
}
